import { LitElement, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import type { GatewayBrowserClient, GatewayHelloOk } from "./gateway";
import { resolveInjectedAssistantIdentity } from "./assistant-identity";
import { loadSettings, type UiSettings } from "./storage";
import type { AuthPromptReason } from "./auth-prompt";
import { renderApp } from "./app-render";
import type { Tab } from "./navigation";
import type { ResolvedTheme, ThemeMode } from "./theme";
import type {
  AgentsListResult,
  ChannelsStatusSnapshot,
  CronJob,
  CronRunLogEntry,
  CronStatus,
  SessionsListResult,
  SkillStatusReport,
} from "./types";
import { type ChatAttachment, type ChatQueueItem, type CronFormState } from "./ui-types";
import type { EventLogEntry } from "./app-events";
import { DEFAULT_CRON_FORM } from "./app-defaults";
import type { ExecApprovalRequest } from "./controllers/exec-approval";
import {
  resetToolStream as resetToolStreamInternal,
  type ToolStreamEntry,
} from "./app-tool-stream";
import {
  handleChatScroll as handleChatScrollInternal,
  resetChatScroll as resetChatScrollInternal,
} from "./app-scroll";
import { connectGateway as connectGatewayInternal } from "./app-gateway";
import {
  handleConnected,
  handleDisconnected,
  handleFirstUpdated,
  handleUpdated,
} from "./app-lifecycle";
import {
  applySettings as applySettingsInternal,
  loadCron as loadCronInternal,
  loadOverview as loadOverviewInternal,
  setTab as setTabInternal,
  setTheme as setThemeInternal,
  onPopState as onPopStateInternal,
} from "./app-settings";
import {
  handleAbortChat as handleAbortChatInternal,
  handleSendChat as handleSendChatInternal,
  removeQueuedMessage as removeQueuedMessageInternal,
} from "./app-chat";
import type { SkillMessage } from "./controllers/skills";
import { loadAssistantIdentity as loadAssistantIdentityInternal } from "./controllers/assistant-identity";
import {
  loadExecApprovals as loadExecApprovalsInternal,
  saveExecApprovals as saveExecApprovalsInternal,
  updateExecApprovalsFormValue as updateExecApprovalsFormValueInternal,
  removeExecApprovalsFormValue as removeExecApprovalsFormValueInternal,
} from "./controllers/exec-approvals";

declare global {
  interface Window {
    __OPENCLAW_CONTROL_UI_BASE_PATH__?: string;
  }
}

const injectedAssistantIdentity = resolveInjectedAssistantIdentity();

function resolveOnboardingMode(): boolean {
  if (!window.location.search) return false;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("onboarding");
  if (!raw) return false;
  const normalized = raw.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on";
}

@customElement("openclaw-app")
export class OpenClawApp extends LitElement {
  @state() settings: UiSettings = loadSettings();
  @state() password = "";
  @state() tab: Tab = "chat";
  @state() onboarding = resolveOnboardingMode();
  @state() connected = false;
  @state() reconnecting = false;
  reconnectErrorTimer: ReturnType<typeof setTimeout> | null = null;
  @state() theme: ThemeMode = this.settings.theme ?? "system";
  @state() themeResolved: ResolvedTheme = "dark";
  @state() hello: GatewayHelloOk | null = null;
  @state() lastError: string | null = null;
  @state() eventLog: EventLogEntry[] = [];
  private eventLogBuffer: EventLogEntry[] = [];
  private toolStreamSyncTimer: number | null = null;
  private sidebarCloseTimer: number | null = null;

  @state() assistantName = injectedAssistantIdentity.name;
  @state() assistantAvatar = injectedAssistantIdentity.avatar;
  @state() assistantAgentId = injectedAssistantIdentity.agentId ?? null;

  @state() sessionKey = this.settings.sessionKey;
  @state() chatLoading = false;
  @state() chatSending = false;
  @state() chatMessage = "";
  @state() chatMessages: unknown[] = [];
  @state() chatToolMessages: unknown[] = [];
  @state() chatStream: string | null = null;
  @state() chatStreamStartedAt: number | null = null;
  @state() chatRunId: string | null = null;
  @state() compactionStatus: import("./app-tool-stream").CompactionStatus | null = null;
  @state() chatAvatarUrl: string | null = null;
  @state() chatThinkingLevel: string | null = null;
  @state() chatQueue: ChatQueueItem[] = [];
  @state() chatAttachments: ChatAttachment[] = [];
  // Sidebar state for tool output viewing
  @state() sidebarOpen = false;
  @state() sidebarContent: string | null = null;
  @state() sidebarError: string | null = null;
  @state() splitRatio = this.settings.splitRatio;

  @state() execApprovalQueue: ExecApprovalRequest[] = [];
  @state() execApprovalBusy = false;
  @state() execApprovalError: string | null = null;
  @state() pendingGatewayUrl: string | null = null;

  @state() showTokenPrompt = false;
  @state() tokenPromptReason: AuthPromptReason | null = null;
  @state() tokenPromptValue = "";

  @state() channelsSnapshot: ChannelsStatusSnapshot | null = null;

  @state() agentsLoading = false;
  @state() agentsList: AgentsListResult | null = null;
  @state() agentsError: string | null = null;

  @state() sessionsLoading = false;
  @state() sessionsResult: SessionsListResult | null = null;
  @state() sessionsError: string | null = null;

  @state() cronLoading = false;
  @state() cronJobs: CronJob[] = [];
  @state() cronStatus: CronStatus | null = null;
  @state() cronError: string | null = null;
  @state() cronForm: CronFormState = { ...DEFAULT_CRON_FORM };
  @state() cronRunsJobId: string | null = null;
  @state() cronRuns: CronRunLogEntry[] = [];
  @state() cronBusy = false;

  @state() skillsLoading = false;
  @state() skillsReport: SkillStatusReport | null = null;
  @state() skillsError: string | null = null;
  @state() skillsFilter = "";
  @state() skillEdits: Record<string, string> = {};
  @state() skillsBusyKey: string | null = null;
  @state() skillMessages: Record<string, SkillMessage> = {};

  // Files page
  @state() filesLoading = false;
  @state() filesList: Array<{ name: string; size: number; mime_type: string; source: string }> = [];
  @state() filesError: string | null = null;

  // Capabilities page
  @state() capabilitiesFilter = "";
  @state() capabilitiesCategoryFilter = "";
  @state() exploreSubTab: import("./navigation").ExploreSubTab = "mcp";

  // MCP servers
  @state() mcpServers: import("./controllers/mcp").McpServerEntry[] = [];
  @state() mcpCatalog: import("./controllers/mcp").McpCatalogEntry[] = [];
  @state() mcpLoading = false;
  @state() mcpError: string | null = null;
  @state() mcpBusyServer: string | null = null;
  @state() mcpMessages: Record<string, import("./controllers/mcp").McpMessage> = {};
  @state() mcpEnvInputs: Record<string, Record<string, string>> = {};

  // MCP setup HITL (inline in chat)
  @state() mcpSetupQueue: import("./controllers/mcp-setup").McpSetupRequest[] = [];
  @state() mcpSetupInputs: Record<string, Record<string, string>> = {};
  @state() mcpSetupBusy: string | null = null;

  // File upload state
  @state() uploading = false;

  // Security settings panel
  @state() securityMode: "locked" | "standard" | "dev" = "standard";
  @state() settingsPanelOpen = false;

  // Voice state
  @state() voiceState: import("./controllers/voice").VoiceState = "idle";
  @state() voiceTranscription = "";
  @state() voiceController: ReturnType<typeof import("./controllers/voice").createVoiceController> | null = null;

  // Exec approvals (Security tab)
  @state() execApprovalsLoading = false;
  @state() execApprovalsSaving = false;
  @state() execApprovalsDirty = false;
  @state() execApprovalsSnapshot: import("./controllers/exec-approvals").ExecApprovalsSnapshot | null = null;
  @state() execApprovalsForm: import("./controllers/exec-approvals").ExecApprovalsFile | null = null;
  @state() execApprovalsSelectedAgent: string | null = null;

  client: GatewayBrowserClient | null = null;
  private chatScrollFrame: number | null = null;
  private chatScrollTimeout: number | null = null;
  private chatHasAutoScrolled = false;
  private chatUserNearBottom = true;
  private toolStreamById = new Map<string, ToolStreamEntry>();
  private toolStreamOrder: string[] = [];
  refreshSessionsAfterChat = false;
  basePath = "";
  private popStateHandler = () =>
    onPopStateInternal(
      this as unknown as Parameters<typeof onPopStateInternal>[0],
    );
  private themeMedia: MediaQueryList | null = null;
  private themeMediaHandler: ((event: MediaQueryListEvent) => void) | null = null;
  private topbarObserver: ResizeObserver | null = null;

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    handleConnected(this as unknown as Parameters<typeof handleConnected>[0]);
  }

  protected firstUpdated() {
    handleFirstUpdated(this as unknown as Parameters<typeof handleFirstUpdated>[0]);
  }

  disconnectedCallback() {
    handleDisconnected(this as unknown as Parameters<typeof handleDisconnected>[0]);
    super.disconnectedCallback();
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    handleUpdated(
      this as unknown as Parameters<typeof handleUpdated>[0],
      changed,
    );
  }

  connect() {
    connectGatewayInternal(
      this as unknown as Parameters<typeof connectGatewayInternal>[0],
    );
  }

  handleChatScroll(event: Event) {
    handleChatScrollInternal(
      this as unknown as Parameters<typeof handleChatScrollInternal>[0],
      event,
    );
  }

  resetToolStream() {
    resetToolStreamInternal(
      this as unknown as Parameters<typeof resetToolStreamInternal>[0],
    );
  }

  resetChatScroll() {
    resetChatScrollInternal(
      this as unknown as Parameters<typeof resetChatScrollInternal>[0],
    );
  }

  async loadAssistantIdentity() {
    await loadAssistantIdentityInternal(this);
  }

  applySettings(next: UiSettings) {
    applySettingsInternal(
      this as unknown as Parameters<typeof applySettingsInternal>[0],
      next,
    );
  }

  setTab(next: Tab) {
    setTabInternal(this as unknown as Parameters<typeof setTabInternal>[0], next);
  }

  setTheme(next: ThemeMode, context?: Parameters<typeof setThemeInternal>[2]) {
    setThemeInternal(
      this as unknown as Parameters<typeof setThemeInternal>[0],
      next,
      context,
    );
  }

  async loadOverview() {
    await loadOverviewInternal(
      this as unknown as Parameters<typeof loadOverviewInternal>[0],
    );
  }

  async loadCron() {
    await loadCronInternal(
      this as unknown as Parameters<typeof loadCronInternal>[0],
    );
  }

  async handleAbortChat() {
    await handleAbortChatInternal(
      this as unknown as Parameters<typeof handleAbortChatInternal>[0],
    );
  }

  removeQueuedMessage(id: string) {
    removeQueuedMessageInternal(
      this as unknown as Parameters<typeof removeQueuedMessageInternal>[0],
      id,
    );
  }

  async handleSendChat(
    messageOverride?: string,
    opts?: Parameters<typeof handleSendChatInternal>[2],
  ) {
    await handleSendChatInternal(
      this as unknown as Parameters<typeof handleSendChatInternal>[0],
      messageOverride,
      opts,
    );
  }

  async handleExecApprovalDecision(decision: "allow-once" | "allow-always" | "deny") {
    const active = this.execApprovalQueue[0];
    if (!active || !this.client || this.execApprovalBusy) return;
    this.execApprovalBusy = true;
    this.execApprovalError = null;
    try {
      await this.client.request("exec.approval.resolve", {
        id: active.id,
        decision,
      });
      this.execApprovalQueue = this.execApprovalQueue.filter((entry) => entry.id !== active.id);
    } catch (err) {
      this.execApprovalError = `Exec approval failed: ${String(err)}`;
    } finally {
      this.execApprovalBusy = false;
    }
  }

  handleGatewayUrlConfirm() {
    const nextGatewayUrl = this.pendingGatewayUrl;
    if (!nextGatewayUrl) return;
    this.pendingGatewayUrl = null;
    applySettingsInternal(
      this as unknown as Parameters<typeof applySettingsInternal>[0],
      { ...this.settings, gatewayUrl: nextGatewayUrl },
    );
    this.connect();
  }

  handleGatewayUrlCancel() {
    this.pendingGatewayUrl = null;
  }

  handleTokenSubmit() {
    if (!this.tokenPromptValue.trim()) return;
    applySettingsInternal(
      this as unknown as Parameters<typeof applySettingsInternal>[0],
      { ...this.settings, token: this.tokenPromptValue.trim() },
    );
    this.showTokenPrompt = false;
    this.tokenPromptReason = null;
    this.tokenPromptValue = "";
    this.connect();
  }

  handleTokenDismiss() {
    this.showTokenPrompt = false;
    this.tokenPromptReason = null;
    this.tokenPromptValue = "";
    this.connect();
  }

  // Sidebar handlers for tool output viewing
  handleOpenSidebar(content: string) {
    if (this.sidebarCloseTimer != null) {
      window.clearTimeout(this.sidebarCloseTimer);
      this.sidebarCloseTimer = null;
    }
    this.sidebarContent = content;
    this.sidebarError = null;
    this.sidebarOpen = true;
  }

  handleCloseSidebar() {
    this.sidebarOpen = false;
    // Clear content after transition
    if (this.sidebarCloseTimer != null) {
      window.clearTimeout(this.sidebarCloseTimer);
    }
    this.sidebarCloseTimer = window.setTimeout(() => {
      if (this.sidebarOpen) return;
      this.sidebarContent = null;
      this.sidebarError = null;
      this.sidebarCloseTimer = null;
    }, 200);
  }

  handleSplitRatioChange(ratio: number) {
    const newRatio = Math.max(0.4, Math.min(0.7, ratio));
    this.splitRatio = newRatio;
    this.applySettings({ ...this.settings, splitRatio: newRatio });
  }

  async updateSecurityMode(mode: "locked" | "standard" | "dev") {
    const token = this.settings.token;
    if (!token) return;
    const base = (this.settings.gatewayUrl?.replace(/^ws/, "http") ?? "").replace(/\/ws\/?$/, "");
    try {
      const resp = await fetch(`${base}/api/security/mode`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ mode }),
      });
      if (resp.ok) {
        const data = await resp.json();
        this.securityMode = data.mode ?? mode;
      }
    } catch {
      // Silently fail — the UI already updated optimistically
    }
  }

  async loadSecurityMode() {
    const token = this.settings.token;
    if (!token) return;
    const base = (this.settings.gatewayUrl?.replace(/^ws/, "http") ?? "").replace(/\/ws\/?$/, "");
    try {
      const resp = await fetch(`${base}/api/security/mode`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        this.securityMode = data.mode ?? "standard";
      }
    } catch {
      // Keep default
    }
  }

  async loadExecApprovalsData() {
    await loadExecApprovalsInternal(this as unknown as import("./controllers/exec-approvals").ExecApprovalsState);
  }

  async saveExecApprovalsData() {
    await saveExecApprovalsInternal(this as unknown as import("./controllers/exec-approvals").ExecApprovalsState);
  }

  updateExecApprovalsField(path: Array<string | number>, value: unknown) {
    updateExecApprovalsFormValueInternal(
      this as unknown as import("./controllers/exec-approvals").ExecApprovalsState,
      path,
      value,
    );
  }

  removeExecApprovalsField(path: Array<string | number>) {
    removeExecApprovalsFormValueInternal(
      this as unknown as import("./controllers/exec-approvals").ExecApprovalsState,
      path,
    );
  }

  render() {
    return renderApp(this);
  }
}
